import * as React from 'react';

import { useLazyQuery } from '@apollo/client';
import GetReportDownloadUrl from '@graphql/queries/GetReportDownloadUrl';
import { PictureAsPdf, CloudDownload } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import DOMPurify from 'dompurify';
import { useSelector } from 'react-redux';
import { isAzure } from 'utils/environmentUtils';

import store from '../../../constants/store';
import selectSessionToken from '../../../selectors/sessionToken';

// Guards against DOM XSS when programmatically navigating an anchor element:
// only allow http(s) URLs to reach the `href`/click sink so a compromised or
// malformed backend response (e.g. a `javascript:` URI) can never execute.
const isSafeDownloadUrl = (url: unknown): url is string => {
  if (typeof url !== 'string' || url.length === 0) {
    return false;
  }
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
};

export async function downloadPDFAzure(reportId: string, sessionToken: string) {
  try {
    const response = await fetch(`/conchBatch/pdfDownload/${reportId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${sessionToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to download PDF: ${response.statusText}`);
    }

    // Create blob from response
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    // Trigger download
    const a = document.createElement('a');
    a.download = `report_${reportId}.pdf`;
    a.href = url;
    a.click();

    // Clean up
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading PDF:', error);
    throw error;
  }
}

interface ReportProps {
  reportId: string;
  pdfIcon?: boolean;
}

const ReportDownloadIcon: React.FC<ReportProps> = (props) => {
  const { reportId, pdfIcon = false } = props;
  const [reportAvailable, setReportAvailable] = React.useState<boolean | null>(
    null,
  );
  const [isDownloading, setIsDownloading] = React.useState(false);
  const sessionToken = useSelector(selectSessionToken) as string | undefined;

  const ggEnvironment = store?.getState?.()?.ggEnvironment;
  const IS_AZURE = isAzure(ggEnvironment);

  const [getReport] = useLazyQuery(GetReportDownloadUrl, {
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
    context: {
      fetchOptions: {
        headers: {
          'Cache-Control': 'no-store',
        },
      },
    },
  })

  React.useEffect(() => {
    const checkReportAvailability = async () => {
      if (reportId && sessionToken) {
        if (!IS_AZURE) {
          const reportUrl = await getReport({
            variables: {
              reportId: reportId
            }
          })
          try {
            const response = await fetch(reportUrl.data.reportPdfUrl, {
              method: 'GET',
              headers: {
                Range: 'bytes=0-0'
              }
            })
            setReportAvailable(response.ok && response.status == 206)
          } catch (error) {
            console.error('Error checking report availability (AWS): ', error)
          }
        } else {
          // Azure handling
          try {
            // Use HEAD request to check if PDF exists
            const response = await fetch(`/conchBatch/pdfDownload/${reportId}`, {
              method: 'HEAD',
              headers: {
                'Authorization': `Bearer ${sessionToken}`,
              },
            });
            setReportAvailable(response.ok && response.status === 200);
          } catch (error) {
            console.error('Error checking report availability:', error);
            setReportAvailable(false);
          }
        }
      }
    };

    checkReportAvailability();
  }, [reportId, getReport, sessionToken]);

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!sessionToken || !reportId || isDownloading) return;

    setIsDownloading(true);
    try {
      if (IS_AZURE) {
        await downloadPDFAzure(reportId, sessionToken);
      } else {
        const reportUrl = await getReport({
          variables: {
            reportId: reportId,
          }
        })
        const pdfUrl = reportUrl.data?.reportPdfUrl;
        if (!isSafeDownloadUrl(pdfUrl)) {
          console.error('Received an invalid or unsafe PDF download URL');
          return;
        }
        const a = document.createElement('a')
        const e = new MouseEvent('click')
        a.download = 'download.pdf'
        a.href = DOMPurify.sanitize(pdfUrl)
        a.dispatchEvent(e)
      }
    } catch (error) {
      console.error('Failed to download PDF:', error);
      // Optionally show error toast/notification to user
    } finally {
      setIsDownloading(false);
    }
  };

  if (reportId === null || reportAvailable === null || !sessionToken) {
    return null;
  }

  return (
    <IconButton
      id="pdf-report-link"
      size="small"
      onClick={handleClick}
      disabled={!reportAvailable || isDownloading}
    >
      {pdfIcon ? (
        <PictureAsPdf
          fontSize="medium"
          color={reportAvailable && !isDownloading ? 'primary' : 'disabled'}
        />
      ) : (
        <CloudDownload
          fontSize="medium"
          color={reportAvailable && !isDownloading ? 'primary' : 'disabled'}
        />
      )}
    </IconButton>
  );
};

export default ReportDownloadIcon;
